import axios, { AxiosRequestConfig } from 'axios';
import { useState } from 'react';
import { useConfig } from '../../config';
import { useTranslation } from '../../i18n/i18n';
import { useSession } from '../../state/session';
import { Value, isDate } from '../Calendar/Calendar';
import { CalendarSelect } from '../CalendarSelect/CalendarSelect';
import { ContentCard } from '../ContentCard/ContentCard';
import { Button } from '../Form/Button';
import { Layout } from '../Layout/Layout';
import { Loader } from '../Loader/Loader';
import { Main } from '../Main/Main';
import { Routing } from '../Routes';
import { Table } from '../Table/Table';
import { formatCalendarDate } from '../subscriptions/utils';
import { filterEmptyValues } from '../utils';
import './ExportStatisticsPage.css';

const axiosConf: AxiosRequestConfig = {
	withCredentials: true,
	responseType: 'text',
};

export const ExportStatisticsPage = (): JSX.Element => {
	const t = useTranslation().exportStatistics;
	const { user } = useSession();
	const { api } = useConfig();
	const [endDate, setEndDate] = useState(formatCalendarDate(new Date()));
	const [startDate, setStartDate] = useState('');
	const [report, setReport] = useState('');
	// const [xls, setXls] = useState('');
	const [loading, setLoading] = useState(false);

	const formatCsvArray = (csv: string): (string[] | undefined)[] =>
		csv
			.trim()
			.split('\n')
			.map((item) => item.match(/(".*?"|[^";\s]+)(?=\s*;|\s*$)/g)?.map((col) => col.replace(/"/g, '')));

	async function generateReport() {
		try {
			setReport('');
			setLoading(true);
			const payload = filterEmptyValues({ startDate, endDate });
			const res = await axios.post(`${api}me/statistics.csv`, payload, axiosConf);

			setReport(res.data);
		} catch (error) {
			console.error('Error downloading CSV file:', error);
		} finally {
			setLoading(false);
		}
	}

	async function download(extension: string) {
		const payload = filterEmptyValues({ startDate, endDate });
		const res = await axios.post(`${api}me/statistics.${extension}`, payload, axiosConf);

		const type = extension === 'csv' ? 'text/csv' : 'application/vnd.ms-excel';
		const data = res.data;

		const blob = new Blob([data], { type });
		const url = window.URL.createObjectURL(blob);
		const now = new Date();
		const tempLink = document.createElement('a');

		tempLink.href = url;
		tempLink.setAttribute('download', `user_order_statistic_${user?.id}_${now.toISOString()}.${extension}`);

		document.body.appendChild(tempLink);
		tempLink.click();

		document.body.removeChild(tempLink);
		window.URL.revokeObjectURL(url);
	}

	const onChangeEndDate = (value: Value): void => setEndDate(value && isDate(value) ? formatCalendarDate(value) : '');

	const onChangeStartDate = (value: Value): void =>
		setStartDate(value && isDate(value) ? formatCalendarDate(value) : '');

	const csvArray = formatCsvArray(report);
	const keys = csvArray.shift();

	return (
		<Main>
			<ContentCard title={t.title}>
				<div className="export-form">
					<Layout type="two-pane">
						<div>
							<CalendarSelect
								label={t.startDate}
								onChangeDate={onChangeStartDate}
								dateValue={startDate}
								todayLabel={t.today}
								placeholder={t.pickStartDate}
								maxDate={new Date(endDate)}
							/>
						</div>
						<div>
							<CalendarSelect
								label={t.endDate}
								minDate={new Date(startDate)}
								maxDate={new Date()}
								onChangeDate={onChangeEndDate}
								dateValue={endDate}
								todayLabel={t.today}
								placeholder={t.pickEndDate}
							/>
						</div>
						<div className="card-footer">
							<Button disabled={loading} onClick={() => generateReport()}>
								{t.generateReport}
							</Button>
						</div>

						{!!report && (
							<div>
								<div className="card-footer">
									<Button disabled={loading} onClick={() => download('csv')}>
										{t.downloadCsv}
									</Button>
								</div>
								<div className="card-footer">
									<Button disabled={loading} onClick={() => download('xls')}>
										{t.downloadXls}
									</Button>
								</div>
							</div>
						)}
					</Layout>
					{loading && <Loader size="icon" />}
					{!!report && (
						<Table className="statistics-table">
							<thead>
								<tr>{keys?.map((key) => <th key={key}>{key}</th>)}</tr>
							</thead>
							<tbody>
								{csvArray.map((row, index) => (
									<tr key={index}>{row?.map((col, o) => <td key={index + o}>{col}</td>)}</tr>
								))}
							</tbody>
						</Table>
					)}
				</div>
			</ContentCard>
		</Main>
	);
};

export const exportStatisticsRouting: Routing = {
	path(urls) {
		return urls.exportStatistics;
	},
	component: ExportStatisticsPage,
	requiresAuth: true,
};
